import React, { useState, useEffect } from "react";
import {
    CircularProgress as MuiCircularProgress,
    LinearProgress as MuiLinearProgress,
    Box as MuiBox,
    Typography as MuiTypography,
    IconButton as MuiIconButton,
    Tooltip as MuiTooltip,
    Alert as MuiAlert,
    Fade as MuiFade,
    useTheme
} from "@mui/material";
import { Close as CloseIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Refresh as RefreshIcon } from "@mui/icons-material";

// Types for progress indicator configurations
export interface CircularProgressProps {
    size?: number;
    color?: "primary" | "secondary" | "inherit";
    thickness?: number;
    value?: number;
    variant?: "determinate" | "indeterminate";
    showStatusText?: boolean;
    statusText?: string;
    duration?: number;
    onCancel?: () => void;
    cancelTooltip?: string;
    "aria-label"?: string;
}

export interface LinearProgressProps {
    variant?: "determinate" | "indeterminate" | "buffer";
    value?: number;
    valueBuffer?: number;
    color?: "primary" | "secondary" | "inherit";
    showStatusText?: boolean;
    statusText?: string;
    step?: string;
    totalSteps?: number;
    currentStep?: number;
    duration?: number;
    onCancel?: () => void;
    cancelTooltip?: string;
    placement?: "global" | "local";
    "aria-label"?: string;
}

export interface ProgressState {
    status: "loading" | "success" | "error" | "cancelled";
    message?: string;
    canRetry?: boolean;
    onRetry?: () => void;
}

// Circular Progress Component for Micro-interactions
export const CircularProgressIndicator: React.FC<CircularProgressProps & ProgressState> = ({
    size = 24,
    color = "primary",
    thickness = 4,
    value,
    variant = "indeterminate",
    showStatusText = false,
    statusText,
    duration = 0,
    onCancel,
    cancelTooltip = "Cancel operation",
    status,
    message,
    canRetry = false,
    onRetry,
    "aria-label": ariaLabel,
    ...props
}) => {
    const theme = useTheme();
    const [showStatusAfterDelay, setShowStatusAfterDelay] = useState(false);

    // Show status text after 3 seconds if duration is long
    useEffect(() => {
        if (duration > 3000 && showStatusText && status === "loading") {
            const timer = setTimeout(() => setShowStatusAfterDelay(true), 3000);
            return () => clearTimeout(timer);
        }
    }, [duration, showStatusText, status]);

    // Success state
    if (status === "success") {
        return (
            <MuiFade in timeout={300}>
                <MuiBox
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "success.main"
                    }}
                    role="status"
                    aria-label="Operation completed successfully"
                >
                    <CheckCircleIcon sx={{ fontSize: size }} />
                    {showStatusText && message && (
                        <MuiTypography variant="body2" sx={{ color: "success.main" }}>
                            {message}
                        </MuiTypography>
                    )}
                </MuiBox>
            </MuiFade>
        );
    }

    // Error state
    if (status === "error") {
        return (
            <MuiFade in timeout={300}>
                <MuiBox
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "error.main"
                    }}
                    role="alert"
                    aria-label="Operation failed"
                >
                    <ErrorIcon sx={{ fontSize: size }} />
                    {showStatusText && message && (
                        <MuiTypography variant="body2" sx={{ color: "error.main" }}>
                            {message}
                        </MuiTypography>
                    )}
                    {canRetry && onRetry && (
                        <MuiTooltip title="Retry operation" arrow>
                            <MuiIconButton
                                size="small"
                                onClick={onRetry}
                                sx={{
                                    color: "error.main",
                                    "&:hover": { bgcolor: "error.light" }
                                }}
                                aria-label="Retry operation"
                            >
                                <RefreshIcon sx={{ fontSize: size * 0.8 }} />
                            </MuiIconButton>
                        </MuiTooltip>
                    )}
                </MuiBox>
            </MuiFade>
        );
    }

    // Loading state
    return (
        <MuiBox
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                position: "relative"
            }}
            role="progressbar"
            aria-label={ariaLabel || statusText || "Loading"}
            aria-valuenow={variant === "determinate" ? value : undefined}
            aria-valuemin={variant === "determinate" ? 0 : undefined}
            aria-valuemax={variant === "determinate" ? 100 : undefined}
        >
            <MuiBox sx={{ position: "relative" }}>
                <MuiCircularProgress size={size} color={color} thickness={thickness} value={value} variant={variant} {...props} />
                {onCancel && (
                    <MuiTooltip title={cancelTooltip} arrow>
                        <MuiIconButton
                            size="small"
                            onClick={onCancel}
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: size * 0.6,
                                height: size * 0.6,
                                bgcolor: "background.paper",
                                "&:hover": { bgcolor: "action.hover" }
                            }}
                            aria-label="Cancel operation"
                        >
                            <CloseIcon sx={{ fontSize: size * 0.4 }} />
                        </MuiIconButton>
                    </MuiTooltip>
                )}
            </MuiBox>

            {showStatusText && (statusText || showStatusAfterDelay) && (
                <MuiFade in timeout={300}>
                    <MuiTypography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            fontSize: "0.875rem"
                        }}
                    >
                        {statusText}
                    </MuiTypography>
                </MuiFade>
            )}
        </MuiBox>
    );
};

// Linear Progress Component for Extended Operations
export const LinearProgressIndicator: React.FC<LinearProgressProps & ProgressState> = ({
    variant = "indeterminate",
    value,
    valueBuffer,
    color = "primary",
    showStatusText = false,
    statusText,
    step,
    totalSteps,
    currentStep,
    duration = 0,
    onCancel,
    cancelTooltip = "Cancel operation",
    placement = "local",
    status,
    message,
    canRetry = false,
    onRetry,
    "aria-label": ariaLabel,
    ...props
}) => {
    const theme = useTheme();
    const [showStatusAfterDelay, setShowStatusAfterDelay] = useState(false);

    // Show status text after 3 seconds if duration is long
    useEffect(() => {
        if (duration > 3000 && showStatusText && status === "loading") {
            const timer = setTimeout(() => setShowStatusAfterDelay(true), 3000);
            return () => clearTimeout(timer);
        }
    }, [duration, showStatusText, status]);

    // Generate step text
    const getStepText = () => {
        if (step && totalSteps && currentStep) {
            return `Step ${currentStep}/${totalSteps}: ${step}`;
        }
        return statusText;
    };

    // Success state
    if (status === "success") {
        return (
            <MuiFade in timeout={300}>
                <MuiAlert
                    severity="success"
                    variant="filled"
                    sx={{
                        position: placement === "global" ? "fixed" : "relative",
                        top: placement === "global" ? 0 : "auto",
                        left: placement === "global" ? 0 : "auto",
                        right: placement === "global" ? 0 : "auto",
                        zIndex: placement === "global" ? theme.zIndex.appBar : "auto",
                        borderRadius: placement === "global" ? 0 : undefined
                    }}
                    role="status"
                    aria-label="Operation completed successfully"
                >
                    {message || "Operation completed successfully"}
                </MuiAlert>
            </MuiFade>
        );
    }

    // Error state
    if (status === "error") {
        return (
            <MuiFade in timeout={300}>
                <MuiAlert
                    severity="error"
                    variant="filled"
                    action={
                        canRetry && onRetry ? (
                            <MuiIconButton color="inherit" size="small" onClick={onRetry} aria-label="Retry operation">
                                <RefreshIcon />
                            </MuiIconButton>
                        ) : undefined
                    }
                    sx={{
                        position: placement === "global" ? "fixed" : "relative",
                        top: placement === "global" ? 0 : "auto",
                        left: placement === "global" ? 0 : "auto",
                        right: placement === "global" ? 0 : "auto",
                        zIndex: placement === "global" ? theme.zIndex.appBar : "auto",
                        borderRadius: placement === "global" ? 0 : undefined
                    }}
                    role="alert"
                    aria-label="Operation failed"
                >
                    {message || "Operation failed"}
                </MuiAlert>
            </MuiFade>
        );
    }

    // Loading state
    return (
        <MuiBox
            sx={{
                position: placement === "global" ? "fixed" : "relative",
                top: placement === "global" ? 0 : "auto",
                left: placement === "global" ? 0 : "auto",
                right: placement === "global" ? 0 : "auto",
                zIndex: placement === "global" ? theme.zIndex.appBar : "auto",
                bgcolor: placement === "global" ? "background.paper" : "transparent",
                borderBottom: placement === "global" ? 1 : 0,
                borderColor: placement === "global" ? "divider" : "transparent",
                pb: placement === "global" ? 1 : 0
            }}
            role="progressbar"
            aria-label={ariaLabel || getStepText() || "Loading"}
            aria-valuenow={variant === "determinate" ? value : undefined}
            aria-valuemin={variant === "determinate" ? 0 : undefined}
            aria-valuemax={variant === "determinate" ? 100 : undefined}
        >
            <MuiLinearProgress
                variant={variant}
                value={value}
                valueBuffer={valueBuffer}
                color={color}
                sx={{
                    height: 4,
                    borderRadius: 2,
                    "& .MuiLinearProgress-bar": {
                        borderRadius: 2
                    }
                }}
                {...props}
            />

            {(showStatusText || showStatusAfterDelay) && (
                <MuiBox
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 1,
                        px: placement === "global" ? 2 : 0
                    }}
                >
                    <MuiFade in timeout={300}>
                        <MuiTypography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem"
                            }}
                        >
                            {getStepText()}
                        </MuiTypography>
                    </MuiFade>

                    {onCancel && (
                        <MuiTooltip title={cancelTooltip} arrow>
                            <MuiIconButton
                                size="small"
                                onClick={onCancel}
                                sx={{
                                    color: "text.secondary",
                                    "&:hover": { bgcolor: "action.hover" }
                                }}
                                aria-label="Cancel operation"
                            >
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </MuiIconButton>
                        </MuiTooltip>
                    )}
                </MuiBox>
            )}
        </MuiBox>
    );
};

// Utility hook for managing progress states
export const useProgressState = (initialStatus: ProgressState["status"] = "loading") => {
    const [progressState, setProgressState] = useState<ProgressState>({
        status: initialStatus
    });

    const setLoading = (message?: string) => {
        setProgressState({ status: "loading", message });
    };

    const setSuccess = (message?: string) => {
        setProgressState({ status: "success", message });
    };

    const setError = (message?: string, canRetry?: boolean, onRetry?: () => void) => {
        setProgressState({ status: "error", message, canRetry, onRetry });
    };

    const setCancelled = (message?: string) => {
        setProgressState({ status: "cancelled", message });
    };

    return {
        progressState,
        setProgressState,
        setLoading,
        setSuccess,
        setError,
        setCancelled
    };
};

// Compact Progress View for Work Product Generation
export interface CompactProgressViewProps {
    currentSection?: number;
    totalSections?: number;
    progress?: number;
    message?: string;
    spinnerSize?: number;
}

export const CompactProgressView: React.FC<CompactProgressViewProps> = ({ currentSection, totalSections, progress = 0, message, spinnerSize = 16 }) => {
    // Generate display message - filter out "Waiting for..." messages and replace "Processing" with "Generating"
    const isWaitingMessage = message && message.toLowerCase().startsWith('waiting for');

    let displayMessage: string;
    if (isWaitingMessage) {
        // Replace "Waiting for..." with section info or "Generating..."
        displayMessage = currentSection && totalSections ? `Generating section ${currentSection} of ${totalSections}` : "Generating...";
    } else if (message) {
        // Replace "Processing" with "Generating" in the message
        displayMessage = message.replace(/^Processing/i, 'Generating');
    } else {
        // Fallback to section info or "Generating..."
        displayMessage = currentSection && totalSections ? `Generating section ${currentSection} of ${totalSections}` : "Generating...";
    }

    return (
        <MuiBox
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "calc(100% - 15px)", // Full width minus 15px on right
                pr: 2 // Padding right for spacing
            }}
        >
            {/* Circular Spinner */}
            <MuiBox
                sx={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                <MuiCircularProgress size={spinnerSize} thickness={3} color="primary" />
            </MuiBox>

            {/* Text and Progress Bar */}
            <MuiBox
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: 1,
                    minWidth: 0
                }}
            >
                {/* Status Text */}
                <MuiTypography
                    variant="body2"
                    sx={{
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        color: "text.primary",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                    }}
                >
                    {displayMessage}
                </MuiTypography>

                {/* Progress Bar */}
                <MuiBox
                    sx={{
                        width: "100%",
                        height: 4,
                        bgcolor: "grey.200",
                        borderRadius: 2,
                        overflow: "hidden"
                    }}
                >
                    <MuiBox
                        sx={{
                            width: `${Math.min(Math.max(progress, 0), 100)}%`,
                            height: "100%",
                            bgcolor: "#103699", // Dark blue color
                            transition: "width 0.3s ease-in-out",
                            borderRadius: 2
                        }}
                    />
                </MuiBox>
            </MuiBox>
        </MuiBox>
    );
};

export default {
    CircularProgressIndicator,
    LinearProgressIndicator,
    CompactProgressView,
    useProgressState
};
